import React from 'react';
import { useFormContext } from 'react-hook-form';
import { clsx } from 'clsx';
import { useLanguage } from '../../../features/language/useLanguage';
import type { InventoryModalFormData } from './types';

interface Props {
  showStatus: boolean;
  isRoastedBulkRestrictedEdit: boolean;
  readOnly: boolean;
}

export const BasicInformationSection: React.FC<Props> = ({ showStatus, isRoastedBulkRestrictedEdit, readOnly }) => {
  const { t } = useLanguage();
  const { register, formState: { errors } } = useFormContext<InventoryModalFormData>();

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-medium text-gray-900 border-b pb-2">{t('inventory.modal.sections.basicInformation')}</h2>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700">{t('inventory.modal.fields.code')}</label>
          <input
            type="text"
            {...register('code')}
            className="bg-gray-100 mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 cursor-not-allowed text-gray-500"
            placeholder={t('inventory.modal.fields.codeAuto')}
            disabled={true}
            data-test-id="inventory-create-code"
          />
          {errors.code && <p className="mt-1 text-xs text-red-600">{errors.code.message}</p>}
        </div>

        <div data-field="name">
          <label className="block text-sm font-medium text-gray-700">{t('inventory.modal.fields.name')}<span className="text-red-500">*</span></label>
          <input
            type="text"
            {...register('name', {
              maxLength: {
                value: 100,
                message: t('inventory.modal.validation.max100')
              }
            })}
            maxLength={100}
            className={clsx(
              'mt-1 block w-full rounded-md shadow-sm sm:text-sm border p-2 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed',
              errors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
            )}
            disabled={readOnly}
            data-test-id="inventory-create-name"
          />
          {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>}
        </div>

        {showStatus && (
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('inventory.modal.fields.status')}</label>
            <select
              {...register('status')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              data-test-id="inventory-create-status"
              disabled={readOnly}
            >
              <option value="active">{t('inventory.modal.status.active')}</option>
              <option value="inactive">{t('inventory.modal.status.inactive')}</option>
            </select>
            {errors.status && <p className="mt-1 text-xs text-red-600">{errors.status.message}</p>}
          </div>
        )}

        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700">{t('inventory.modal.fields.description')}</label>
          <textarea
            {...register('description')}
            rows={3}
            disabled={isRoastedBulkRestrictedEdit || readOnly}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            data-test-id="inventory-create-description"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('inventory.modal.fields.location')}</label>
          <input
            type="text"
            {...register('location')}
            placeholder={t('inventory.modal.fields.locationPlaceholder')}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            data-test-id="inventory-create-location"
            disabled={readOnly}
          />
        </div>
      </div>
    </div>
  );
};
